import { useTranslation } from 'react-i18next';
import { Card, Tooltip } from '@mui/material';
import './NodesConfiguration.scss';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import Debug from 'debug';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { DataGrid, enUS, frFR, GridCellParams, GridColDef, GridToolbar, MuiEvent } from '@mui/x-data-grid';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { AuthHeader } from '../../../utils/helpers/auth-headers';
import { AlertSeverity } from '../../../utils/enum/Alerts';
import { setAlertData } from '../../../stores/alertStore';
import { bytesToSize, Worker } from '../../../models/Worker';
import { Alarm } from '../../../models/Alarm';
import { PageRowsOptions } from '../../../models/DataGridConf';
import { RootState } from '../../../stores/globalStore';
import { setConnectedUser } from '../../../stores/authenticationStore';

const debug = Debug('monitoring:workers');

export default function Workers() {
  const { t } = useTranslation();

  const i18nLang = i18next.language;
  const [gridLang, setGridLang] = useState(frFR);

  const connectedUser = useSelector((state: RootState) => state.authentication.connectedUser);

  const [selectionModel, setSelectionModel] = useState<any[]>([]);
  const paramsRoute = useParams();
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const [pathName, setPathName] = useState<string>(path);
  const dispatch = useDispatch();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [pageSize, setPageSize] = useState<number>(connectedUser.prefs?.nbRow ? connectedUser.prefs.nbRow : 50);
  const pageRowsOptions = PageRowsOptions;
  const [alarms, setAlarms] = useState<Alarm[]>([]);

  const getPexipWorker = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/workers`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        debug('pexip-worker : ', response.data);
        setWorkers(response.data.data);
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  const getAlarms = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/alarms`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setAlarms(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
        }
        console.error(error);
      });
  };

  useEffect(() => {
    if (i18nLang === 'en') {
      setGridLang(enUS);
    } else {
      setGridLang(frFR);
    }
  }, [i18nLang]);

  useEffect(() => {
    setPathName(path);
    getPexipWorker();
    getAlarms();
  }, [path]);

  useEffect(() => {
    if (!path.includes('/configuration/')) {
      setSelectionModel([]);
    } else {
      setSelectionModel([paramsRoute.workerId]);
    }
  }, [pathName]);

  const hasAlarms = (worker: Worker) => {
    for (const alarm of alarms) {
      if (alarm.node === worker.address) {
        return true;
      }
    }
    return false;
  };

  const getColorAlert = (worker: Worker) => {
    for (const alarm of alarms) {
      if (alarm.node === worker.address && alarm.level === 'error') {
        return 'is-invalid';
      }
    }
    return 'is-warning';
  };

  const columns: GridColDef[] = [
    {
      field: 'id', headerName: 'ID', minWidth: 90, flex: 1, hide: true
    },
    {
      field: 'name',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.name') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.name')}</span>
        </Tooltip>
      ),
      description: 'name of worker',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (<strong>{hasAlarms(params.row) && <FontAwesomeIcon className={getColorAlert(params.row)} icon={faWarning} />} {params.row.name}</strong>)
    },
    {
      field: 'address',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.address') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.address')}</span>
        </Tooltip>
      ),
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'nodeType',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.nodeType') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.nodeType')}</span>
        </Tooltip>
      ),
      flex: 1
    },
    {
      field: 'systemLocation',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.systemLocation') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.systemLocation')}</span>
        </Tooltip>
      ),
      flex: 1
    },
    {
      field: 'version',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.version') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.version')}</span>
        </Tooltip>
      ),
      flex: 1
    },
    {
      field: 'cpuCount',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.cpuCount') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.cpuCount')}</span>
        </Tooltip>
      ),
      flex: 1
    },
    {
      field: 'totalRam',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.totalRam') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.totalRam')}</span>
        </Tooltip>
      ),
      flex: 1,
      renderCell: (params) => (<span>{bytesToSize(params.row.totalRam)}</span>)
    },
    {
      field: 'maintenanceMode',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.maintenanceMode') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.maintenanceMode')}</span>
        </Tooltip>
      ),
      flex: 1,
      renderCell: (params) => {
        if (params.row.maintenanceMode) {
          return (<span>{ t('InstanceConfiguration.worker.yes') }</span>);
        }
        return (<span>{ t('InstanceConfiguration.worker.no') }</span>);
      }
    },
    {
      field: 'syncStatus',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.syncStatus') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.syncStatus')}</span>
        </Tooltip>
      ),
      flex: 1,
      renderCell: (params) => {
        if (params.row.syncStatus === 'SYNCED') {
          return (<span className="is-valid">{params.row.syncStatus}</span>);
        }
        return (<span className="is-invalid">{params.row.syncStatus}</span>);
      }
    },
    {
      field: 'lastReported',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.lastReported') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.lastReported')}</span>
        </Tooltip>
      ),
      flex: 1,
      renderCell: (params) => new Date(params.row.lastReported).toLocaleString(i18nLang)
    },
    {
      field: 'maxCalls',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.maxCalls') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.maxCalls')}</span>
        </Tooltip>
      ),
      flex: 1,
      renderCell: (params) => (<span>{params.row.maxHdCalls} HD - {params.row.maxSdCalls} SD</span>)
    },
    {
      field: 'mediaLoad',
      renderHeader: () => (
        <Tooltip title={t('InstanceConfiguration.worker.mediaLoad') as string} arrow>
          <span className="strong-header">{t('InstanceConfiguration.worker.mediaLoad')}</span>
        </Tooltip>
      ),
      flex: 1,
      renderCell: (params) => (<span>{params.row.mediaLoad} %</span>)
    }
  ];

  const updateNbRowUser = (updateNbRow: number) => {  
    const updatePrefs = { ...connectedUser.prefs };
    updatePrefs.nbRow = updateNbRow;
    const modifyPrefs = {
      prefs: updatePrefs
    };
    axios.post(`${window._env_.REACT_APP_API_URI}/user/prefs`, modifyPrefs, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        dispatch(setConnectedUser({ connectedUser: response.data.data }));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const changePageSize = (newPage: number) => {
    setPageSize(newPage);
    updateNbRowUser(newPage);
  };

  return (
    <div className={pathName.includes('/configuration/') ? 'configuration-bloc outlet-open' : 'configuration-bloc'}>
      <div className="category-container">
        <Card>
          <div className="title-node">
            <strong>{t('InstanceConfiguration.worker.title')}</strong>
          </div>
          <DataGrid
            getRowId={(worker) => worker.id}
            rows={workers}
            columns={columns}
            rowsPerPageOptions={pageRowsOptions}
            pageSize={pageSize}
            onPageSizeChange={(newPage) => changePageSize(newPage)}
            components={{ Toolbar: GridToolbar }}
            pagination
            onCellClick={(params: GridCellParams, event: MuiEvent<React.MouseEvent>) => {
              event.preventDefault();
              navigate(`./${params.row.id}`);
            }}
            getRowClassName={() => 'pointer'}
            hideFooterSelectedRowCount
            onSelectionModelChange={setSelectionModel}
            selectionModel={selectionModel}
            autoHeight
            localeText={gridLang.components.MuiDataGrid.defaultProps.localeText}
            density="compact"
          />
        </Card>
      </div>
      <div className="category-container">
        <Outlet />
      </div>
    </div>
  );
}
